/** @jsxImportSource @emotion/react */

import React, { FC, useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import { format, utcToZonedTime } from 'date-fns-tz'
import { useApolloClient, useMutation } from '@apollo/client'
import MuiTextareaAutosize from '@mui/material/TextareaAutosize'
import { FormControlLabel } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import { ReactComponent as SearchIcon } from 'components/Autosuggest/icons/search.svg'
import Table, { FiltersWrapper, PaginationPage } from 'components/Table'
import { findOneUser_findOneUser as User } from '__generatedTypes__/findOneUser'
import Loader from 'components/Loader'
import { BLOCK_USER_BY_ADMIN, FORCE_USER_LOGOUT, UNBLOCK_USER_BY_ADMIN } from 'queries/users'
import { forceUserLogout, forceUserLogoutVariables } from '__generatedTypes__/forceUserLogout'
import { blockUserByAdmin, blockUserByAdminVariables } from '__generatedTypes__/blockUserByAdmin'
import { unblockUserByAdmin, unblockUserByAdminVariables } from '__generatedTypes__/unblockUserByAdmin'
import * as textAreaClasses from 'components/TextareaAutosize/styles'
import * as inputClasses from 'components/Input/styles'
import BaseModal from 'components/Modals/BaseModal'
import { toast } from 'utils/toast'
import { useAuthContext } from 'utils/auth'
import Checkbox from 'components/Checkbox'
import { findAllIpLog_findAllIpLog_items } from '__generatedTypes__/findAllIpLog'
import * as tabsClasses from '../styles'
import * as classes from './styles'
import { useIpLogs } from './useIpLogs'
import { DetailedIpLogModalContent } from './DetailedIpLogModalContent'

const timeZone = 'America/New_York'

export const INITIAL_REASON =
  'Your account has been detected for account sharing. Please reach out to support@boosterprep.com to contact our team.'

type Props = {
  user: User
}

export const UserManagement: FC<Props> = ({ user }) => {
  const { isAdmin } = useAuthContext()
  const { id: userId, isBlocked } = user
  const apolloClient = useApolloClient()

  const [detailedIpLogItem, setDetailedIpLogItem] = useState<findAllIpLog_findAllIpLog_items | null>(null)
  const [blockReason, setBlockReason] = useState(() => user.blockReason ?? INITIAL_REASON)
  const [isMobileAppSelected, setIsMobileAppSelected] = useState(false)
  const [isWebSelected, setIsWebSelected] = useState(true)

  useEffect(() => {
    setBlockReason(user.blockReason ?? INITIAL_REASON)
  }, [user.blockReason])

  const [forceLogout, { loading: isForceLogoutRequestInFlight }] = useMutation<
    forceUserLogout,
    forceUserLogoutVariables
  >(FORCE_USER_LOGOUT)
  const [blockUser, { loading: isBlockingRequestInFlight }] = useMutation<blockUserByAdmin, blockUserByAdminVariables>(
    BLOCK_USER_BY_ADMIN
  )
  const [unBlockUser, { loading: isUnBlockingRequestInFlight }] = useMutation<
    unblockUserByAdmin,
    unblockUserByAdminVariables
  >(UNBLOCK_USER_BY_ADMIN)

  const ipLogsState = useIpLogs(userId)

  const { items, count, loading, pagination, refetch } = ipLogsState

  useEffect(() => {
    refetch({ filters: { isMobileAppSelected, isWebSelected } })
  }, [refetch, isMobileAppSelected, isWebSelected])

  const handleBlockUserClick = (isBlockedUpdated: boolean) => async () => {
    const options = {
      variables: {
        userId,
        reason: blockReason,
      },
    }
    const action = !isBlockedUpdated ? unBlockUser : blockUser

    try {
      await action(options)

      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: 'UserType',
          id: userId,
        }),
        fields: {
          isBlocked: () => isBlockedUpdated,
          blockReason: () => (isBlockedUpdated ? blockReason : ''),
        },
      })

      if (!isBlockedUpdated) {
        setBlockReason('')
      }

      if (isBlockedUpdated === isBlocked) {
        toast.success(`Message updated successfully`)
      } else {
        toast.success(`User ${isBlockedUpdated ? 'blocked' : 'unblocked'} successfully`)
      }
    } catch (error) {
      toast.error(`Failed to ${isBlockedUpdated ? 'unblock' : 'block'} user`)
    }
  }

  const handleForceLogoutClick = async () => {
    try {
      const { data } = await forceLogout({
        variables: {
          userId: userId,
        },
      })

      if (data?.forceUserLogout) {
        toast.success('Forced user logout successfully')
      } else {
        toast.error('Failed to force logout user')
      }
    } catch (error) {
      toast.error('Failed to force logout user')
    }
  }

  const handleDetailedIpLogModalClose = () => setDetailedIpLogItem(null)

  const renderIpLogTable = () => {
    if (!isMobileAppSelected && !isWebSelected) {
      return <Typography>No device types selected</Typography>
    }

    if (loading) {
      return <Loader />
    }

    if (!items.length) {
      return <Typography>No ip logs found</Typography>
    }

    return (
      <div css={classes.tableWrapper}>
        <Table
          css={classes.table}
          columns={[
            {
              title: 'IP Address',
              renderCell: (ipLog) => <span>{ipLog.ip}</span>,
              flex: 1,
            },
            {
              title: 'Device ID',
              renderCell: (ipLog) => <span>{ipLog.browserId}</span>,
              flex: 1,
            },
            {
              title: 'City',
              renderCell: (ipLog) => <span>{ipLog.city}</span>,
              flex: 1,
            },
            {
              title: 'Region',
              renderCell: (ipLog) => <span>{ipLog.region}</span>,
              flex: 1,
            },
            {
              title: 'Date + Time (EST)',
              renderCell: (ipLog) => (
                <span>
                  {/* Somehow using this without utcToZonedTime is not working */}
                  {format(utcToZonedTime(new Date(ipLog.createdAt), timeZone), 'MM/dd/yyyy', { timeZone })} -{' '}
                  {format(utcToZonedTime(new Date(ipLog.createdAt), timeZone), 'h:mm a', { timeZone })}
                </span>
              ),
              flex: 1,
            },
            {
              renderCell: (ipLog) => (
                <IconButton
                  size="small"
                  onClick={() => setDetailedIpLogItem(ipLog)}
                  disabled={!ipLog.browserId && !ipLog.userAgent}
                  css={classes.detailedIpLogButton}
                >
                  <SearchIcon />
                </IconButton>
              ),
              flex: '0 1 36px',
            },
          ]}
          items={items}
          wrapperMinWidth={800}
          wrapperPadding={8}
        />
        <div css={classes.tableFooter}>
          <FiltersWrapper>
            <span>{count ?? 0} items</span>
          </FiltersWrapper>
          <PaginationPage {...pagination} />
        </div>
      </div>
    )
  }

  return (
    <section css={tabsClasses.container}>
      <div css={tabsClasses.form}>
        <Typography variant="title3" css={tabsClasses.title}>
          User Management
        </Typography>
        <Divider />
        <FormControlLabel
          control={
            <MuiTextareaAutosize
              css={[textAreaClasses.textArea, classes.reasonInput]}
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Enter message shown to student"
              disabled={!isAdmin}
            />
          }
          label="Message shown to student"
          labelPlacement="top"
          css={inputClasses.labelWrapper}
        />

        <div css={classes.buttonsContainer}>
          {isBlocked && (
            <Button
              variant="outlined"
              size="large"
              fullWidth
              disabled={!isAdmin || !blockReason || isBlockingRequestInFlight || isUnBlockingRequestInFlight}
              onClick={handleBlockUserClick(isBlocked)}
            >
              Update message
            </Button>
          )}

          <Button
            variant="outlined"
            color={isBlocked ? 'error' : 'blue'}
            size="large"
            fullWidth
            disabled={
              !isAdmin || (!isBlocked && !blockReason) || isBlockingRequestInFlight || isUnBlockingRequestInFlight
            }
            onClick={handleBlockUserClick(!isBlocked)}
          >
            {isBlocked ? 'Unblock Account' : 'Block Account'}
          </Button>
        </div>

        <Divider />
        <Button
          variant="outlined"
          color="blue"
          size="large"
          fullWidth
          disabled={!isAdmin || isForceLogoutRequestInFlight}
          onClick={handleForceLogoutClick}
        >
          Force Logout
        </Button>
        <Divider />
        <Typography variant="title3" css={[tabsClasses.title, tabsClasses.titleWithFilters]}>
          IP log
          <div>
            <Checkbox
              checked={isMobileAppSelected}
              onChange={() => setIsMobileAppSelected(!isMobileAppSelected)}
              label="Mobile"
              className="mobile-app-checkbox"
              data-testid="mobile-app-checkbox"
            />
            <Checkbox
              checked={isWebSelected}
              onChange={() => setIsWebSelected(!isWebSelected)}
              label="Desktop/Laptop"
              className="web-checkbox"
              data-testid="web-checkbox"
            />
          </div>
        </Typography>
        {renderIpLogTable()}

        <BaseModal open={!!detailedIpLogItem} onClose={handleDetailedIpLogModalClose}>
          <DetailedIpLogModalContent detailedIpLogItem={detailedIpLogItem} />
        </BaseModal>
      </div>
    </section>
  )
}
